import {
  Alert,
  Badge,
  Button,
  Col,
  Collapse,
  Empty,
  Row,
  Spin,
  Table,
  Tag,
} from "antd";
import { FormInstance } from "antd/lib/form/Form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React, { Fragment, useRef, useState } from "react";
import ReactHTMLParser from "react-html-parser";
import BaseForm from "../components/Form/BaseForm";
import entitlements from "../config/entitlements";
import {
  Activity,
  Attachment,
  CloseInteractionPayload,
  Company,
  Customer,
  FormFieldConfig,
  InteractionNotificationPreview,
  InteractionTicket,
  Note,
  SelectOptionsInterface,
  SubCategory,
  Subscriber,
  SubscriberListItem,
  Template,
  Ticket,
  TransferRequest,
  User,
} from "../helpers/shared-interfaces";
import {
  generateFirstSelectOption,
  generateDisplayErrorMessage,
  getStateColor,
  handleValidationErrors,
  injectKey,
  request,
} from "../helpers/utilities";
import {
  buildCloseCompanyInteractionFormFields,
  buildCloseCompanyInteractionPayload,
  buildCloseInteractionFormFields,
  buildCloseInteractionPayload,
  isTransferWithinTemplate,
} from "../helpers/interaction";
import ActivitiesSection, {
  CALCULATE_DUE_DATE_QUERY,
  GET_SUBSCRIBERS,
  GET_SUB_CATEGORIES,
} from "./ActivitiesSection";
import AttachmentsSection from "./AttachmentsSection";
import EntitledSection from "./EntitledSection";
import NotesSection from "./NotesSection";
import { ALL_USERS_QUERY } from "../gql/users";
import { tabPaneStyle, DEBOUNCE_TIME } from "../helpers/constants";
import { GET_TEMPLATES } from "../gql/templates";
import { GET_TEMPLATE_OPTIONS } from "../gql/templates";
import {
  GET_COMPANY_INTERACTION_NOTIFICATION_PREVIEW,
  GET_CUSTOMER_INTERACTION_NOTIFICATION_PREVIEW,
} from "../gql/interactions";
import ModalCreator from "./ModalCreator";

const { Panel } = Collapse;

interface TicketCreateFormProps {
  reference: React.RefObject<FormInstance>;
  customer?: Customer;
  loading: boolean;
  createTicket: () => Promise<void>;
  currentNote?: Note;
}

interface TicketCreateFormState {
  fieldsToHide: string[];
  subcribers: SubscriberListItem[];
  mentions: number[];
  users: User[];
  q: string;
  subCategoryQuery: string;
  subCategories: SubCategory[];
  ticketSubCategoryDueDate: string;
}

export class TicketCreateForm extends React.Component<
  TicketCreateFormProps,
  TicketCreateFormState
> {
  constructor(props: TicketCreateFormProps) {
    super(props);
    this.state = {
      fieldsToHide: [],
      subcribers: [],
      mentions: [],
      users: [],
      q: "",
      subCategoryQuery: "",
      subCategories: [],
      ticketSubCategoryDueDate: "",
    };
  }

  componentDidMount = (): void => {
    this.getSubscriberList();
    this.getAllUsers();
    this.getSubCategories();
    if (!this.props.customer) {
      this.setState({ fieldsToHide: ["firstName", "lastName"] });
    }
  };

  getSubscriberList = (): void => {
    request({
      query: GET_SUBSCRIBERS,
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          subscribers: [];
        };
      }>): void => {
        this.setState({
          subcribers: data.data.subscribers,
        });
      },
    );
  };

  getAllUsers = (): void => {
    const { q } = this.state;
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
      });
    });
  };

  triggerDelayedSearch = debounce(async (forUser = false): Promise<void> => {
    forUser ? await this.getAllUsers() : await this.getSubCategories();
  }, DEBOUNCE_TIME);

  handleSearchInput = async (q: string): Promise<void> => {
    this.setState({ q }, () => {
      this.triggerDelayedSearch(true);
    });
  };

  getSubCategories = (): void => {
    const { subCategoryQuery } = this.state;
    request({
      query: GET_SUB_CATEGORIES,
      variables: {
        page: 1,
        q: subCategoryQuery,
      },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          subCategories: {
            results: SubCategory[];
          };
        };
      }>): void => {
        this.setState({
          subCategories: data.data.subCategories.results,
        });
      },
    );
  };

  handleSubCategorySearchInput = async (q: string): Promise<void> => {
    this.setState({ subCategoryQuery: q }, () => {
      this.triggerDelayedSearch();
    });
  };

  ticketSubCategorySelect = async (subCategoryId: string): Promise<void> => {
    await request({
      query: CALCULATE_DUE_DATE_QUERY,
      variables: {
        subCategoryId,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        ticketSubCategoryDueDate: data.calculateSubCategoryDueDate,
      });
    });
  };

  render() {
    const { subcribers, users, subCategories, ticketSubCategoryDueDate } =
      this.state;

    const modifiedSubscribers: Subscriber[] = subcribers.map((sub) => {
      return { ...sub, id: sub.id, name: sub.user.name };
    });

    const sourceOptions: SelectOptionsInterface[] = [
      { id: "zammad", name: "Zammad" },
    ];

    const typeOptions: SelectOptionsInterface[] = [
      { id: "Request for Change", name: "Request for Change" },
      { id: "Incident", name: "Incident" },
      { id: "Problem", name: "Problem" },
    ];

    const modifiedUsers = generateFirstSelectOption("Select Assignee").concat(
      users.map((user) => {
        return { ...user, id: user.id, name: user.name };
      }),
    );

    const modifiedSubCategories: SelectOptionsInterface[] = subCategories.map(
      (sub: SubCategory) => {
        return { ...sub, id: sub?.id, name: sub?.longName || sub?.name };
      },
    );

    const fields: FormFieldConfig[] = [
      {
        id: "source",
        name: "source",
        inputType: "select",
        rule: "string",
        selectOptions: sourceOptions,
        initialValue: "zammad",
        placeholder: "Select Source",
        label: "Source",
        required: true,
        colConfig: {
          span: 7,
        },
      },
      {
        id: "firstName",
        name: "firstName",
        inputType: "string",
        rule: "string",
        placeholder: "First Name",
        label: "First Name",
        initialValue: this.props.customer?.firstName,
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "lastName",
        name: "lastName",
        inputType: "string",
        rule: "string",
        placeholder: "Last Name",
        label: "Last Name",
        initialValue: this.props.customer?.lastName,
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "type",
        name: "type",
        inputType: "select",
        rule: "string",
        selectOptions: typeOptions,
        initialValue: "Problem",
        placeholder: "Select type",
        label: "Type",
        required: true,
        colConfig: {
          span: 7,
          offset: this.props.customer ? 0 : 1,
        },
      },
      {
        id: "subject",
        name: "subject",
        inputType: "string",
        rule: "string",
        placeholder: "Subject",
        label: "Subject",
        initialValue: "",
        required: true,
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "description",
        name: "description",
        inputType: "string",
        rule: "string",
        placeholder: "Description",
        label: "Description",
        initialValue: this.props.currentNote?.description ?? "",
        required: true,
        colConfig: {
          offset: this.props.customer ? 1 : 0,
          span: 7,
        },
      },
      {
        id: "mentions",
        name: "mentions",
        placeholder: "Mentions",
        required: false,
        inputType: "multipleSelect",
        rule: "string",
        label: "Mentions",
        isArrayField: true,
        selectOptions: modifiedSubscribers,
        colConfig: {
          offset: this.props.customer ? 0 : 1,
          span: 7,
        },
      },
      {
        id: "assigneeId",
        name: "assigneeId",
        placeholder: "Assignee",
        required: true,
        inputType: "search",
        rule: "string",
        label: "Assignee",
        isSearching: true,
        selectOptions: modifiedUsers,
        changeHandler: this.handleSearchInput,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          offset: 1,
          span: 7,
        },
      },
      {
        id: "ticketSubCategoryId",
        name: "ticketSubCategoryId",
        placeholder: "Ticket SubCategory",
        required: true,
        inputType: "search",
        rule: "string",
        label: "Ticket Sub Category",
        isSearching: true,
        selectOptions: modifiedSubCategories,
        changeHandler: this.handleSubCategorySearchInput,
        selectHandler: this.ticketSubCategorySelect,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 11,
        },
      },
      {
        id: "ticketSubCategoryDueDate",
        name: "ticketSubCategoryDueDate",
        placeholder: "Select a sub-category",
        required: true,
        inputType: "description",
        disabled: true,
        rule: "string",
        label: "Ticket Sub Category Due Date",
        value: ticketSubCategoryDueDate,
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          offset: 1,
          span: 11,
        },
      },
      {
        name: "createTicket",
        value: "Create Ticket",
        inputType: "button",
        required: false,
        elementStyle: {
          position: "relative",
          top: "19px",
          borderRadius: 0,
          height: "33px",
        },
        className: "base-button",
        handleClick: this.props.createTicket,
        colConfig: {
          span: 5,
        },
      },
    ];

    return (
      <Row>
        <Col>
          <Spin spinning={this.props.loading}>
            <EntitledSection
              entitlement={entitlements.BusinessDevelopment.interactions.create}
            >
              <div className="view-opportunity-row create-interaction-row">
                <Col>
                  <BaseForm
                    layout="vertical"
                    reference={this.props.reference}
                    fields={fields}
                    fieldsToHide={this.state.fieldsToHide}
                  ></BaseForm>
                </Col>
              </div>
            </EntitledSection>
          </Spin>
        </Col>
      </Row>
    );
  }
}

interface InteractionProps {
  interactionTickets: InteractionTicket[];
  tickets: Ticket[];
}

interface InteractionState {
  tickets: Ticket[];
  isLoading: boolean;
}

export const columns = [
  {
    title: "Ticket Number",
    dataIndex: "id",
    key: "id",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Subject",
    dataIndex: ["properties", "subject"],
    key: "Subject",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "Description",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "First Name",
    dataIndex: "first_name",
    key: "firstName",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Last Name",
    dataIndex: "last_name",
    key: "lastName",
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Ticket Url",
    dataIndex: "url",
    key: "url",
    render(text: string): JSX.Element {
      return (
        <a href={text} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      );
    },
  },
  {
    title: "State",
    dataIndex: "state",
    key: "state",
    render(text: string): JSX.Element {
      return (
        <Tag color={getStateColor(text)}>
          {text}
        </Tag>
      );
    },
  },
];
export class InteractionTickets extends React.Component<
  InteractionProps,
  InteractionState
> {
  render(): JSX.Element {
    const { interactionTickets, tickets } = this.props;
    const interactionActualTickets = interactionTickets.map(
      (interactionTicket) => interactionTicket.ticket,
    );
    const combinedTickets = [...interactionActualTickets, ...tickets].map(
      (ticket) => {
        return {
          ...ticket?.properties,
          id: ticket?.id,
          url: ticket?.url,
          number: ticket?.number,
          state: ticket?.state,
        };
      },
    );

    return (
      <>
        <div>
          <Row>
            <Col>
              <EntitledSection
                entitlement={entitlements.BusinessDevelopment.interactions.list}
              >
                {combinedTickets.length > 0 && (
                  <div
                    style={{
                      margin: "20px 0",
                    }}
                  >
                    <h1
                      style={{
                        marginBottom: "20px",
                        color: "rgba(0, 0, 0, 0.85)",
                        fontWeight: "bold",
                        fontSize: "16px",
                        lineHeight: "1.5",
                      }}
                    >
                      Tickets
                    </h1>
                    <Table
                      bordered={true}
                      columns={columns}
                      dataSource={injectKey(combinedTickets, "id")}
                      pagination={{
                        defaultPageSize: 10,
                        total: combinedTickets.length,
                      }}
                    />
                  </div>
                )}
              </EntitledSection>
            </Col>
          </Row>
        </div>
      </>
    );
  }
}

interface CloseInteractionFormProps {
  reference: React.RefObject<FormInstance>;
  interactionId: string;
  forCustomer: boolean;
  onSubmit: () => Promise<void>;
}

interface CloseInteractionFormState {
  closureType: "" | "inbound" | "outbound";
  templates: Template[];
  isLoadingTemplates: boolean;
  isPreviewLoading: boolean;
  preview?: InteractionNotificationPreview;
  previewError?: string;
  selectedTemplate?: Template;
  transferRequestId?: number;
  customComment?: string;
  recipientOverride?: string;
}

class CloseInteractionForm extends React.Component<
  CloseInteractionFormProps,
  CloseInteractionFormState
> {
  constructor(props: CloseInteractionFormProps) {
    super(props);
    this.state = {
      closureType: "",
      templates: [],
      isLoadingTemplates: false,
      isPreviewLoading: false,
    };
  }

  componentDidMount(): void {
    this.fetchTemplates();
  }

  fetchTemplates = async (): Promise<void> => {
    this.setState({ isLoadingTemplates: true });
    try {
      const templateCategoryKeywords = ["outbound", this.props.forCustomer ? "customer" : "company"];
      const {
        data: { data },
      }: AxiosResponse<{ data: { templates: Template[] } }> = await request({
        query: GET_TEMPLATE_OPTIONS,
        variables: { templateCategoryKeywords },
      });

      this.setState({
        templates: (data.templates || []).filter(
          (t) => !/automat/i.test(t.templateCategory?.name ?? "")
        ),
        isLoadingTemplates: false,
      });
    } catch (error) {
      this.setState({
        templates: [],
        isLoadingTemplates: false,
      });
    }
  };

  getPreviewQuery = (): string => {
    return this.props.forCustomer
      ? GET_CUSTOMER_INTERACTION_NOTIFICATION_PREVIEW
      : GET_COMPANY_INTERACTION_NOTIFICATION_PREVIEW;
  };

  fetchPreview = async (
    templateId: string,
    transferRequestId?: number,
    customComment?: string,
    recipientOverride?: string,
  ): Promise<void> => {
    const selectedTemplate = this.state.templates.find(
      (template) => template.id === templateId,
    );

    if (!selectedTemplate) {
      this.setState({
        preview: undefined,
        previewError: undefined,
      });
      return;
    }

    if (
      this.props.forCustomer &&
      isTransferWithinTemplate(selectedTemplate) &&
      !transferRequestId
    ) {
      this.setState({
        preview: undefined,
        previewError:
          "Enter a transfer request ID to preview this notification.",
      });
      return;
    }

    this.setState({
      isPreviewLoading: true,
      previewError: undefined,
    });

    try {
      const variables = this.props.forCustomer
        ? {
            customerInteractionId: this.props.interactionId,
            templateId,
            transferRequestId,
            customComment: customComment || undefined,
            recipientOverride: recipientOverride || undefined,
          }
        : {
            companyInteractionId: this.props.interactionId,
            templateId,
            customComment: customComment || undefined,
          };

      const { data }: AxiosResponse<any> = await request({
        query: this.getPreviewQuery(),
        variables,
      });

      const previewResponse = this.props.forCustomer
        ? data.data.customerInteractionNotificationPreview
        : data.data.companyInteractionNotificationPreview;

      if (previewResponse.errors?.length > 0) {
        this.setState({
          preview: undefined,
          previewError: generateDisplayErrorMessage(previewResponse.errors),
          isPreviewLoading: false,
        });
        return;
      }

      this.setState({
        preview: previewResponse.preview,
        previewError: undefined,
        isPreviewLoading: false,
      });
    } catch (error) {
      this.setState({
        preview: undefined,
        previewError: "Failed to load notification preview.",
        isPreviewLoading: false,
      });
    }
  };

  handleClosureTypeSelect = (type: string): void => {
    this.setState({
      closureType: type as "inbound" | "outbound",
      preview: undefined,
      previewError: undefined,
      selectedTemplate: undefined,
      transferRequestId: undefined,
      customComment: undefined,
      recipientOverride: undefined,
    });
  };

  handleTemplateSelect = (templateId: string): void => {
    const template = this.state.templates.find((t) => t.id === templateId);
    const { customComment, recipientOverride } = this.state;
    this.setState({
      selectedTemplate: template,
      transferRequestId: undefined,
      preview: undefined,
      previewError: undefined,
    });

    if (template) {
      if (this.props.forCustomer && isTransferWithinTemplate(template)) {
        this.setState({
          previewError:
            "Enter a transfer request ID to preview this notification.",
        });
      } else {
        this.fetchPreview(templateId, undefined, customComment, recipientOverride);
      }
    }
  };

  handleTransferRequestIdChange = (value?: number | null): void => {
    const transferRequestId = typeof value === "number" ? value : undefined;
    const { selectedTemplate, customComment, recipientOverride } = this.state;

    this.setState({ transferRequestId });

    if (selectedTemplate) {
      this.fetchPreview(selectedTemplate.id, transferRequestId, customComment, recipientOverride);
    }
  };

  debouncedFetchPreviewOnTextChange = debounce(
    (templateId: string, transferRequestId: number | undefined, customComment: string | undefined, recipientOverride: string | undefined): void => {
      this.fetchPreview(templateId, transferRequestId, customComment, recipientOverride);
    },
    DEBOUNCE_TIME,
  );

  handleFormValuesChange = (changedValues: Record<string, unknown>): void => {
    const tracksRecipientOverride = this.props.forCustomer;
    const hasCustomCommentChange = "customComment" in changedValues;
    const hasRecipientOverrideChange =
      tracksRecipientOverride && "recipientOverride" in changedValues;

    if (!hasCustomCommentChange && !hasRecipientOverrideChange) return;

    const customComment = (hasCustomCommentChange
      ? (changedValues.customComment as string) || undefined
      : this.state.customComment);
    const recipientOverride = (hasRecipientOverrideChange
      ? (changedValues.recipientOverride as string) || undefined
      : this.state.recipientOverride);

    this.setState({ customComment, recipientOverride });

    const { selectedTemplate, transferRequestId } = this.state;
    if (selectedTemplate) {
      this.debouncedFetchPreviewOnTextChange(selectedTemplate.id, transferRequestId, customComment, recipientOverride);
    }
  };

  render(): JSX.Element {
    const {
      closureType,
      isLoadingTemplates,
      isPreviewLoading,
      preview,
      previewError,
      selectedTemplate,
      templates,
    } = this.state;
    const outboundTemplates = this.props.forCustomer
      ? templates
      : templates.filter((template) => !isTransferWithinTemplate(template));

    const fields: FormFieldConfig[] = this.props.forCustomer
      ? buildCloseInteractionFormFields({
          closureType,
          outboundTemplates,
          selectedTemplate,
          onClosureTypeSelect: this.handleClosureTypeSelect,
          onTemplateSelect: this.handleTemplateSelect,
          onTransferRequestIdChange: this.handleTransferRequestIdChange,
          onCustomCommentChange: () => {},
          onOverrideContactChange: () => {},
          onSubmit: this.props.onSubmit,
        })
      : buildCloseCompanyInteractionFormFields({
          closureType,
          outboundTemplates,
          selectedTemplate,
          onClosureTypeSelect: this.handleClosureTypeSelect,
          onTemplateSelect: this.handleTemplateSelect,
          onSubmit: this.props.onSubmit,
        });

    return (
      <>
        {!this.props.forCustomer && (
          <p>Are you sure you want to close this interaction?</p>
        )}
        <BaseForm
          layout="vertical"
          reference={this.props.reference}
          fields={fields}
          fieldsToHide={[]}
          config={{ onValuesChange: this.handleFormValuesChange }}
        />
        {closureType === "outbound" &&
          !isLoadingTemplates &&
          outboundTemplates.length === 0 && (
            <div style={{ marginTop: "16px" }}>
              <Empty description="no template exist for outbound." />
            </div>
          )}
        {closureType === "outbound" && previewError && (
          <div style={{ marginTop: "16px" }}>
            <Alert type="info" showIcon message={previewError} />
          </div>
        )}
        {isPreviewLoading && (
          <div style={{ marginTop: "16px" }}>
            <Spin spinning={true} />
          </div>
        )}
        {preview && (
          <div style={{ marginTop: "16px" }}>
            <label
              style={{ fontWeight: 600, display: "block", marginBottom: "8px" }}
            >
              Notification Preview
            </label>
            <div
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: "4px",
                padding: "16px",
                background: "#fff",
              }}
            >
              <p style={{ marginBottom: "8px" }}>
                <b>Template:</b> {preview.templateName}
              </p>
              <p style={{ marginBottom: "8px" }}>
                <b>Channel:</b> {preview.notificationType.toUpperCase()}
              </p>
              <p style={{ marginBottom: "8px" }}>
                <b>Recipient:</b> {preview.recipient}
              </p>
              {preview.subject && (
                <p style={{ marginBottom: "16px" }}>
                  <b>Subject:</b> {preview.subject}
                </p>
              )}
              {preview.templateType === "email" ? (
                <div>{ReactHTMLParser(preview.body)}</div>
              ) : (
                <pre
                  style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    fontFamily: "inherit",
                  }}
                >
                  {preview.body}
                </pre>
              )}
            </div>
          </div>
        )}
      </>
    );
  }
}

interface Props {
  forCustomer: boolean;
  forCompany: boolean;
  isEditingInteraction: boolean;
  showTicketForm: boolean;
  isCreateTicketLoading?: boolean;
  toggleInteractionEdit: () => void;
  toggleTicketCreate: () => void;
  toggleViewRequestsSection: (currentActivityId: string) => void;
  toggleViewInteractionSection: (currentActivityId: string) => void;
  toggleViewRedemptionRequestsSection: (currentActivityId: string) => void;
  interaction?: Activity;
  tickets?: Ticket[];
  createInteraction: () => Promise<void>;
  activityReference: React.RefObject<FormInstance>;
  noteReference: React.RefObject<FormInstance>;
  handleNoteUpdate: (notes: Note[]) => void;
  entity: string;
  ticketReference: React.RefObject<FormInstance>;
  createNote: () => Promise<void>;
  createTicket: () => Promise<void>;
  notes: Note[];
  showRequests?: boolean;
  openInteraction: () => Promise<void>;
  holdInteraction: () => Promise<void>;
  closeInteraction: (payload?: CloseInteractionPayload) => Promise<boolean>;
  openDelayUpdateModal?: () => void;
  updateInteraction?: () => Promise<void>;
  createAttachment?: (combinedInfo: string) => Promise<void>;
  audits?: JSX.Element;
  auditCount?: number;
  transferRequests: TransferRequest[];
  customer?: Customer;
  currentNote?: Note;
  company?: Company;
  showInteraction: boolean;
  toggleCreateInteractionModal: () => void;
  isInteractionLoading: boolean;
  updateAttachments: (attachment: Attachment) => void;
  removeAttachment: (id: string) => void;
  assignAttachmentsToCompanyInteraction?: (
    attachmentIds: string[],
    companyInteractionId: string,
  ) => void;
  assignAttachmentsToCustomerInteraction?: (
    attachmentIds: string[],
    companyInteractionId: string,
  ) => void;
  currentInteractionId?: string;
}

export default (props: Props): JSX.Element => {
  const interactions = props.interaction ? [props.interaction] : [];
  const tickets = props.forCustomer
    ? props.interaction?.customerInteractionTickets
    : props.interaction?.companyInteractionTickets;

  const status = props.interaction?.status;
  const showHoldAction = status !== "on_hold";
  const showOpenAction = status !== "open";
  const showCloseAction = status !== "closed";
  const showDelayUpdateAction =
    status === "open" && !!props.openDelayUpdateModal;
  const showStatusActions =
    !!props.interaction &&
    (showHoldAction ||
      showOpenAction ||
      showCloseAction ||
      showDelayUpdateAction);

  const selectedInteractionId = props.interaction?.id || "";
  const transferRequest = (props.transferRequests || []).find(
    (transfer: TransferRequest): boolean =>
      transfer.customerInteraction?.id.includes(selectedInteractionId),
  );
  const transferCondition =
    props.interaction?.subCategory?.longName?.includes("Transfer In") ||
    props.interaction?.subCategory?.longName?.includes("Transfer Out") ||
    props.interaction?.subCategory?.longName?.includes("Merge Accounts");
  const redemptionCondition =
    props.interaction?.subCategory?.longName?.includes("Redemption") ||
    props.interaction?.subCategory?.longName?.includes("redemption");
  const showCreateRequestAction =
    !!props.showRequests &&
    !!props.interaction &&
    ((!!transferCondition && !transferRequest) || !!redemptionCondition);
  const showViewRequestAction =
    !!props.showRequests &&
    !!props.interaction &&
    !!transferCondition &&
    !!transferRequest;

  const [showCloseModal, setShowCloseModal] = useState(false);
  const closeInteractionFormRef = useRef<FormInstance>(null);

  const handleSubmit = async (): Promise<void> => {
    if (!closeInteractionFormRef.current) {
      return;
    }

    try {
      const formValues = await closeInteractionFormRef.current.validateFields();
      const payload = props.forCustomer
        ? buildCloseInteractionPayload(formValues as CloseInteractionPayload)
        : buildCloseCompanyInteractionPayload(
            formValues as CloseInteractionPayload,
          );

      const didClose = await props.closeInteraction(payload);

      if (didClose) {
        setShowCloseModal(false);
      }
    } catch (error) {
      handleValidationErrors(error);
    }
  };

  const attachmentsForCustomer =
    props.interaction?.customerInteractionAttachments?.map(
      (interactionAttachment) => interactionAttachment.attachment,
    ) || [];

  const attachmentsForCompany =
    props.interaction?.companyInteractionAttachments?.map(
      (interactionAttachment) => interactionAttachment.attachment,
    ) || [];

  const attachments = props.forCustomer
    ? attachmentsForCustomer
    : attachmentsForCompany;
  const hasAttachments = attachments.length > 0;

  return (
    <Fragment>
      {(showStatusActions || !!props.interaction) && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          {showHoldAction && (
            <Button className="base-button" onClick={props.holdInteraction}>
              Put Interaction On Hold
            </Button>
          )}
          {showOpenAction && (
            <Button className="base-button" onClick={props.openInteraction}>
              Open Interaction
            </Button>
          )}
          {showDelayUpdateAction && (
            <Button className="base-button" onClick={props.openDelayUpdateModal}>
              Send Delay Update
            </Button>
          )}
          {props.interaction && (
            <>
              {showCreateRequestAction && (
                <Button
                  className="base-button"
                  onClick={() => {
                    if (!props.interaction) return;

                    if (transferCondition) {
                      props.toggleViewRequestsSection(props.interaction.id);
                      return;
                    }

                    props.toggleViewRedemptionRequestsSection(
                      props.interaction.id,
                    );
                  }}
                >
                  Create Request
                </Button>
              )}
              {showViewRequestAction && (
                <Button
                  className="base-button"
                  onClick={() => {
                    if (transferRequest) {
                      window.location.assign(
                        `/transfer-requests/${transferRequest.id}`,
                      );
                    }
                  }}
                >
                  View Request
                </Button>
              )}
              <Button
                className="base-button"
                onClick={props.toggleTicketCreate}
              >
                {props.showTicketForm ? "Close Ticket" : "Create Ticket"}
              </Button>
            </>
          )}
          {props.interaction && (
            <EntitledSection
              entitlement={entitlements.Customers.interactions.edit}
            >
              <Button
                className="base-button"
                onClick={props.toggleInteractionEdit}
              >
                {props.isEditingInteraction ? "Cancel Edit" : "Edit"}
              </Button>
            </EntitledSection>
          )}
          {showCloseAction && (
            <Button
              style={{
                ...(!props.interaction ? { marginLeft: "auto" } : {}),
                backgroundColor: "#fff",
                borderColor: "red",
                color: "red",
              }}
              onClick={() => setShowCloseModal(true)}
            >
              Close Interaction
            </Button>
          )}
        </div>
      )}
      <ActivitiesSection
        showBorder={false}
        showRequests={props.showRequests}
        forCompany={props.forCompany}
        forCustomer={props.forCustomer}
        showHeading={false}
        showInteraction={props.showInteraction}
        isInteractionLoading={props.isInteractionLoading}
        toggleCreateInteractionModal={props.toggleCreateInteractionModal}
        updateInteraction={props.updateInteraction}
        toggleInteractionEdit={props.toggleInteractionEdit}
        toggleViewRequestsSection={props.toggleViewRequestsSection}
        toggleViewInteractionSection={props.toggleViewInteractionSection}
        toggleViewRedemptionRequestsSection={
          props.toggleViewRedemptionRequestsSection
        }
        activities={interactions}
        toggleCreateTicket={props.toggleTicketCreate}
        attachments={[]}
        updateAttachments={props.updateAttachments}
        removeAttachment={props.removeAttachment}
        createInteraction={props.createInteraction}
        reference={props.activityReference}
        showForm={false}
        isEditing={props.isEditingInteraction}
        useBaseColumn={true}
        detailLayout="columns"
        actionLabelMode="closeOnOpen"
        isTicketFormOpen={props.showTicketForm}
        showDetailActions={false}
        transferRequests={props.transferRequests}
        customer={props.customer}
        company={props.company}
        activitiesCount={0}
        nextPage={undefined}
        assignAttachmentsToCompanyInteraction={
          props.assignAttachmentsToCompanyInteraction
        }
        assignAttachmentsToCustomerInteraction={
          props.assignAttachmentsToCustomerInteraction
        }
        currentInteractionId={props.currentInteractionId}
      />
      {props.interaction && hasAttachments && (
        <div style={{ marginTop: "25px" }}>
          <h1
            style={{
              marginBottom: "20px",
              color: "rgba(0, 0, 0, 0.85)",
              fontWeight: "bold",
              fontSize: "16px",
              lineHeight: "1.5",
            }}
          >
            Attachments
          </h1>
          <AttachmentsSection
            reference={props.activityReference}
            createAttachment={props.createAttachment as never}
            attachments={attachments}
            showForm={false}
            handleRemove={props.removeAttachment}
          />
        </div>
      )}
      {props.showTicketForm &&
        ModalCreator({
          title: "Create Ticket",
          okType: "primary",
          okText: "Close",
          width: 900,
          showModal: props.showTicketForm,
          onOkay: props.toggleTicketCreate,
          toggleModal: props.toggleTicketCreate,
          showFooter: false,
          inscription: (
            <TicketCreateForm
              loading={props.isCreateTicketLoading || false}
              reference={props.ticketReference}
              customer={props.customer}
              createTicket={props.createTicket}
              currentNote={props.currentNote}
            />
          ),
        })}

      <InteractionTickets
        interactionTickets={tickets || []}
        tickets={props.tickets || []}
      />
      <Collapse style={{ marginTop: "25px" }}>
        <Panel
          header={
            <span>
              Notes <Badge count={props.notes.length} style={tabPaneStyle} />
            </span>
          }
          key="Notes"
        >
          <NotesSection
            showHeading={true}
            reference={props.noteReference}
            createNote={props.createNote}
            notes={props.notes}
            showForm={true}
            entity={props.entity}
            onNoteUpdate={props.handleNoteUpdate}
            stackFormButton={true}
          />
        </Panel>
        {props.audits && (
          <Panel
            header={
              <span>
                Audit Log{" "}
                {typeof props.auditCount === "number" && (
                  <Badge count={props.auditCount} style={tabPaneStyle} />
                )}
              </span>
            }
            key="AuditLog"
          >
            {props.audits}
          </Panel>
        )}
      </Collapse>

      {ModalCreator({
        title: "Close Interaction",
        showModal: showCloseModal,
        onOkay: handleSubmit,
        toggleModal: () => setShowCloseModal(false),
        showFooter: false,
        width: 760,
        inscription: (
          <CloseInteractionForm
            reference={closeInteractionFormRef}
            interactionId={props.interaction?.id || ""}
            forCustomer={props.forCustomer}
            onSubmit={handleSubmit}
          />
        ),
      })}
    </Fragment>
  );
};
